import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Calendar, MessageCircle, Inbox } from "lucide-react";

const TIERS = ["truth_keeper","truth_seeker","truth_maker"];

export default function AdminEngagement() {
  const [tab, setTab] = useState("polls");
  return (
    <div className="space-y-6" data-testid="admin-engagement">
      <div className="flex gap-2 border-b border-white/10">
        {[
          ["polls","Polls"],
          ["suggestions","Truth Suggestions"],
          ["qa","Live Q&A Schedule"],
        ].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`eng-tab-${k}`}
            className={`px-4 py-2 font-ui text-[10px] tracking-[0.32em] uppercase ${tab===k?"text-[#C9A055] border-b-2 border-[#C9A055]":"text-[#8a857d] hover:text-[#F4F1EA]"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "polls" && <PollsSection/>}
      {tab === "suggestions" && <SuggestionsSection/>}
      {tab === "qa" && <QASection/>}
    </div>
  );
}

function PollsSection() {
  const [polls, setPolls] = useState([]);
  const [creating, setCreating] = useState(null);
  const load = () => http.get("/admin/polls").then(({data})=>setPolls(data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const startNew = () => setCreating({ question: "", min_tier: "truth_seeker", active: true, options: [{key:"a",label:""},{key:"b",label:""}] });
  const addOpt = () => setCreating((c) => ({ ...c, options: [...c.options, {key: String.fromCharCode(97 + c.options.length), label: ""}] }));
  const save = async () => {
    if (!creating.question.trim() || creating.options.some(o => !o.label.trim())) return toast.error("Question + all options required");
    try { await http.post("/admin/polls", creating); toast.success("Poll created"); setCreating(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete poll and all its votes/comments?")) return;
    await http.delete(`/admin/polls/${id}`); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={startNew} data-testid="new-poll-btn" className="px-4 py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2"><Plus size={13}/> New Poll</button>
      </div>
      {polls.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center font-body italic text-[#8a857d]">No polls yet.</div>
      ) : polls.map((p) => (
        <div key={p.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`poll-${p.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-display text-xl text-[#F4F1EA]">{p.question}</div>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mt-1">{p.min_tier?.replace("truth_","")} · {p.total_votes || 0} votes</div>
            </div>
            <button onClick={() => remove(p.id)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]"><Trash2 size={13}/></button>
          </div>
          <div className="mt-3 space-y-1">
            {(p.options || []).map((o) => (
              <div key={o.key} className="flex justify-between text-[12px] text-[#B5B0A7] font-body">
                <span>{o.label}</span><span className="text-[#C9A055]">{o.votes || 0}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setCreating(null)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-lg w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="new-poll-modal">
            <div className="flex justify-between"><div className="font-display text-xl text-[#F4F1EA]">New Poll</div><button onClick={() => setCreating(null)}><X size={18}/></button></div>
            <input className={inputCls} placeholder="Question" value={creating.question} onChange={(e) => setCreating({...creating, question: e.target.value})} data-testid="new-poll-question"/>
            <select className={inputCls} value={creating.min_tier} onChange={(e) => setCreating({...creating, min_tier: e.target.value})} data-testid="new-poll-tier">
              {TIERS.map((t) => <option key={t} value={t}>{t.replace("truth_","")}</option>)}
            </select>
            <div className="space-y-2">
              {creating.options.map((o, i) => (
                <input key={i} className={inputCls} placeholder={`Option ${i+1}`} value={o.label} onChange={(e) => { const next = [...creating.options]; next[i] = {...o, label: e.target.value}; setCreating({...creating, options: next}); }} data-testid={`new-poll-opt-${i}`}/>
              ))}
              <button type="button" onClick={addOpt} className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">+ Add option</button>
            </div>
            <button onClick={save} data-testid="save-poll-btn" className="w-full py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase">Create Poll</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionsSection() {
  const [rows, setRows] = useState([]);
  const load = () => http.get("/admin/truth-suggestions").then(({data}) => setRows(data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Delete suggestion?")) return; await http.delete(`/admin/truth-suggestions/${id}`); load(); };
  return rows.length === 0 ? (
    <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center font-body italic text-[#8a857d]">
      <Inbox size={28} className="mx-auto mb-3 text-[#5a5650]"/>
      No suggestions yet. Truth Makers can submit ideas from their dashboard.
    </div>
  ) : (
    <div className="space-y-3">
      {rows.map((s) => (
        <div key={s.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`suggestion-${s.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-body text-[14px] text-[#F4F1EA] whitespace-pre-line">{s.suggestion}</div>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mt-2">{s.user_name} · {s.user_email} · {new Date(s.created_at).toLocaleDateString()}</div>
            </div>
            <button onClick={() => remove(s.id)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]"><Trash2 size={13}/></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function QASection() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(null);
  const load = () => http.get("/admin/live-qa").then(({data}) => setRows(data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const startNew = () => setCreating({ title: "", scheduled_at: "", meeting_url: "", recording_url: "", description: "" });
  const save = async () => {
    if (!creating.title || !creating.scheduled_at) return toast.error("Title + date required");
    try {
      if (creating.id) await http.put(`/admin/live-qa/${creating.id}`, creating);
      else await http.post("/admin/live-qa", creating);
      toast.success("Saved"); setCreating(null); load();
    } catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete event?")) return; await http.delete(`/admin/live-qa/${id}`); load(); };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={startNew} data-testid="new-qa-btn" className="px-4 py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2"><Plus size={13}/> New Q&A Event</button>
      </div>
      {rows.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center font-body italic text-[#8a857d]"><Calendar size={28} className="mx-auto mb-3 text-[#5a5650]"/> No events scheduled.</div>
      ) : rows.map((e) => (
        <div key={e.id} className="border border-white/10 bg-[#0a0a0a] p-5 flex justify-between items-start gap-3" data-testid={`qa-${e.id}`}>
          <div>
            <div className="font-display text-lg text-[#F4F1EA]">{e.title}</div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase mt-1">{new Date(e.scheduled_at).toLocaleString()}</div>
            {e.meeting_url && <div className="text-[11px] text-[#8a857d] mt-1 break-all">Join: {e.meeting_url}</div>}
            {e.recording_url && <div className="text-[11px] text-[#5fc377] mt-1 break-all">Recording: {e.recording_url}</div>}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setCreating(e)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055]"><Plus size={13} className="rotate-45"/></button>
            <button onClick={() => remove(e.id)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]"><Trash2 size={13}/></button>
          </div>
        </div>
      ))}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setCreating(null)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-lg w-full max-h-[90vh] overflow-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="qa-modal">
            <div className="flex justify-between"><div className="font-display text-xl text-[#F4F1EA]">{creating.id ? "Edit Event" : "New Event"}</div><button onClick={() => setCreating(null)}><X size={18}/></button></div>
            <input className={inputCls} placeholder="Title" value={creating.title} onChange={(e) => setCreating({...creating, title: e.target.value})} data-testid="qa-title"/>
            <input type="datetime-local" className={inputCls} value={(creating.scheduled_at || "").slice(0,16)} onChange={(e) => setCreating({...creating, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : ""})} data-testid="qa-when"/>
            <input className={inputCls} placeholder="Zoom/Meet URL (members will see this)" value={creating.meeting_url || ""} onChange={(e) => setCreating({...creating, meeting_url: e.target.value})} data-testid="qa-url"/>
            <input className={inputCls} placeholder="Recording URL (paste after event)" value={creating.recording_url || ""} onChange={(e) => setCreating({...creating, recording_url: e.target.value})} data-testid="qa-recording"/>
            <textarea rows={3} className={inputCls + " resize-y"} placeholder="Description (optional)" value={creating.description || ""} onChange={(e) => setCreating({...creating, description: e.target.value})} data-testid="qa-desc"/>
            <button onClick={save} data-testid="qa-save-btn" className="w-full py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none";
